import { useMutation, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useSession } from './useSession';
import {
  fetchCardTemplates,
  fetchCards,
  fetchCardTransactions,
  type CardTx,
  cardAccountStatusApi,
  ensureCardAccountApi,
  issueStatusApi,
  renameCardApi,
  setCardStatusApi,
  removeCardApi,
  type ApiCard,
  type CardAccountStatus,
} from '../lib/cards';

/** Per-template Banana registration status — poll while pending (Incpt). */
export function useCardAccount(templateId?: number) {
  const { token } = useSession();
  return useQuery({
    queryKey: ['cardAccount', templateId],
    enabled: !!token && templateId != null,
    queryFn: () => cardAccountStatusApi(templateId!),
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2500 : false),
  });
}

export type { CardAccountStatus };

/** Keeps the full-screen issue state honest across WebSocket disconnects and
 * app reloads; provider webhooks remain the fast path. */
export function useCardIssueStatus() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['cardIssueStatus'],
    enabled: !!token,
    queryFn: issueStatusApi,
    // Keep polling while a paid apply is in flight so a silent success clears
    // the overlay even if the card.list push was missed.
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2500 : false),
  });
}

/**
 * On launch, register Banana users for NoKYC templates (#9, #27, #32) in the background.
 * Email templates (#25) are skipped — the user verifies email at issue time (Incpt).
 */
export function useWarmCardAccounts() {
  const { token } = useSession();
  const { data: templates } = useCardTemplates();
  const qc = useQueryClient();
  const warmedRef = useRef(new Set<number>());

  useEffect(() => {
    if (!token || !templates?.length) return;
    const pending = templates.filter(
      (tpl) =>
        !tpl.requiresEmail &&
        !tpl.poolAvailable &&
        !warmedRef.current.has(tpl.templateId),
    );
    if (pending.length === 0) return;
    pending.forEach((tpl) => warmedRef.current.add(tpl.templateId));

    void (async () => {
      for (const tpl of pending) {
        try {
          const status = await cardAccountStatusApi(tpl.templateId);
          if (status.registered && status.status !== 'rejected') continue;
          await ensureCardAccountApi(tpl.templateId, { waitForApproval: false });
          await qc.invalidateQueries({ queryKey: ['cardAccount', tpl.templateId] });
        } catch {
          // Best-effort warm-up; explicit issue flow retries and surfaces errors.
        }
      }
    })();
  }, [qc, templates, token]);
}

/** The user's cards. Provider webhooks push changes, so there's no polling —
 *  useRealtime refreshes this list when a card event arrives. */
export function useCards() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['cards'],
    enabled: !!token,
    queryFn: fetchCards,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** The cards on sale. Rarely changes, so it's cached for the session. */
export function useCardTemplates() {
  const { token } = useSession();
  return useQuery({
    queryKey: ['cardTemplates'],
    enabled: !!token,
    queryFn: fetchCardTemplates,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Card transactions for one card, newest first, paginated for infinite scroll. */
export function useCardTransactions(cardId: string | undefined) {
  const { token } = useSession();
  const q = useInfiniteQuery({
    queryKey: ['cardTx', cardId],
    enabled: !!token && !!cardId,
    queryFn: ({ pageParam }) => fetchCardTransactions(cardId!, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => {
    if (!q.data) return undefined;
    const seen = new Set<string>();
    const out: CardTx[] = [];
    for (const page of q.data.pages) {
      for (const t of page.transactions) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [q.data]);

  return {
    data,
    isLoading: q.isLoading,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    fetchNextPage: q.fetchNextPage,
  };
}

/** Card edits. Each writes the updated card straight into the list so the screen
 *  reflects it immediately, without waiting for a refetch. */
export function useCardActions() {
  const qc = useQueryClient();
  const put = (card: ApiCard) =>
    qc.setQueryData<ApiCard[]>(['cards'], (old) => old?.map((c) => (c.id === card.id ? card : c)) ?? old);

  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) => renameCardApi(id, label),
    onSuccess: put,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, action, reauthToken }: { id: string; action: 'freeze' | 'unfreeze' | 'close'; reauthToken?: string }) =>
      setCardStatusApi(id, action, reauthToken),
    onSuccess: put,
  });

  const remove = useMutation({
    mutationFn: ({ id, reauthToken }: { id: string; reauthToken?: string }) => removeCardApi(id, reauthToken),
    onSuccess: (_r, { id }) =>
      qc.setQueryData<ApiCard[]>(['cards'], (old) => old?.filter((c) => c.id !== id) ?? old),
  });

  return { rename, setStatus, remove };
}
